import {
  Button,
  Stack,
  Tab,
  Tabs,
  Typography,
  Link,
  Chip,
  Radio,
  Box,
  TextField,
} from "@mui/material";
import {
  CustomTabPanel,
  CustomTable,
  CustomeDialog,
  DeleteModal,
  Loader,
  PageListLayout,
  ServerPaginatedtable,
  InputControl,
} from "../../../shared";
import { CustomDialogTemplate } from "../../../shared/components/CustomDialogTemplate";
import { useMemo, useRef, useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useStores } from "../hooks";
import { createColumnHelper } from "@tanstack/react-table";
import { SubRowCustomTable } from "../components";
import { useSyncHistoryList } from "../hooks";
import moment from "moment";

export const SyncHistoryList = () => {
  let { id } = useParams();

  const {
    listQuery,
    state,
    pagination,
    setPagination,
    sorting,
    setSorting,
    search,
    setSearchState,
    moduleName,
    onSearchHandler,
    onDateChangeHandler,
    tableData,
    onChangeStatusHandler,
    disableclearButton,
    onClearFilter,
    handleMoreModal,
  } = useSyncHistoryList(id);

  const { dataList, isLoading } = state;
  const columnHelper = createColumnHelper();
  const navigate = useNavigate();

  const statusOptions = [
    { id: 1, name: "SUCCESS" },
    { id: 0, name: "FAILED" },
  ];

  const RemarkCell = ({ value }) => {
    const [isTextOverflowing, setIsTextOverflowing] = useState(false);
    const textRef = useRef(null);

    useEffect(() => {
      if (textRef.current) {
        const isOverflowing =
          textRef.current.scrollWidth > textRef.current.clientWidth;
        setIsTextOverflowing(isOverflowing);
      }
    }, [textRef]);

    return (
      <Box
        sx={{ display: "flex", alignItems: "center", zIndex: 1 }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Typography
          ref={textRef}
          variant="subtitle2"
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "400px",
          }}
        >
          {value}
        </Typography>
        {isTextOverflowing && (
          <Button
            variant="text"
            size="small"
            sx={{
              height: "28px !important",
              border: "none !important",
              p: "6px 8px",
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleMoreModal(true, value);
            }}
          >
            More
          </Button>
        )}
      </Box>
    );
  };

  const columns = [
    columnHelper.accessor("sync_date", {
      header: () => "Sync Date",
      cell: ({ getValue }) => moment(getValue()).format("DD-MM-YYYY, hh:mm A"),
    }),

    columnHelper.accessor("status", {
      header: () => "Status",
      cell: ({ getValue }) => {
        const success = getValue() === 1;
        return (
          <Chip
            size="small"
            color={success ? "primary" : "errorChip"}
            variant="filled"
            label={success ? "SUCCESS" : "FAILED"}
          />
        );
      },
    }),

    columnHelper.accessor("user", {
      header: () => "User",
    }),

    columnHelper.accessor("remarks", {
      header: () => "Remarks",
      cell: ({ getValue }) => {
        const text = getValue();
        if (!text) return "--";

        return <RemarkCell value={text} />;
      },
    }),

    columnHelper.display({
      id: "actions",
      cell: (props) => {
        const { row } = props;
        const formattedDate = moment(row?.original?.sync_date).format(
          "YYYY-MM-DD",
        );
        return (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap="8px"
          >
            <img
              alt="details"
              src="/icons/arrow_forward.png"
              onClick={(e) => navigate(`sync-details/${row?.original?.id}`)}
            />
          </Stack>
        );
      },
    }),
  ];

  return (
    <>
      <ServerPaginatedtable
        defaultData={listQuery?.data?.history || []}
        columns={columns}
        isLoading={listQuery.isLoading}
        pagination={pagination}
        setPagination={(callback) =>
          setPagination({ tableName: moduleName, callback })
        }
        sorting={sorting}
        setSorting={(callback) =>
          setSorting({ tableName: moduleName, callback })
        }
        onSearchChange={onSearchHandler}
        search={search}
        paginationProps={
          listQuery.data?.pagination || { total: 0, lastPage: 1 }
        }
        headerText={
          <Typography variant="h5" fontWeight={500} fontSize="18px">
            Sync History
          </Typography>
        }
        onRowLink={(e) => {
          const formattedDate = moment(e.sync_date).format("YYYY-MM-DD");
          return `sync-details/${e.id}`;
        }}
        tableHeaderContent={
          <Stack
            sx={{
              flexDirection: "row",
              gap: "6px",
              width: "100%",
            }}
          >
            <InputControl
              type="datePicker"
              size="small"
              label="Date"
              customDatePickerIcon
              datePickerProps={{
                disableMaskedInput: true,
                inputFormat: "dd, MMM yyyy",
                // maxDate: addDays(new Date(), 1),s
                value: tableData?.syncHistory?.data?.selectedDate || null,
                onChange: (event) => onDateChangeHandler(event),
              }}
            />
            <InputControl
              type="dropdown"
              variant="filter"
              size="small"
              options={statusOptions}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{
                width: "180px",
              }}
              value={
                statusOptions?.find(
                  (option) =>
                    option.id === tableData.syncHistory.data.selectedStatus,
                ) || null
              }
              renderInput={(params) => (
                <TextField {...params} placeholder="Status" />
              )}
              onChange={(event, option) => onChangeStatusHandler(option)}
            />

            <Button
              type="button"
              size="small"
              variant="text"
              sx={{
                p: "6px 12px 6px 12px",
                gap: "6px",
              }}
              disabled={disableclearButton}
              onClick={onClearFilter}
            >
              Clear All
            </Button>
          </Stack>
        }
      />
      <CustomDialogTemplate
        PaperProps={{
          sx: {
            minWidth: { md: "200PX ", lg: "400px", xs: "100%" },
            height: "auto",
          },
        }}
        title="Remarks"
        closeIcon={false}
        open={state.isOpenMoreModal}
        content={
          <Stack justifyContent="flex-start">
            <Box sx={{ p: 2, width: "100%" }}>{state.remarks}</Box>
            <Stack
              direction="row"
              justifyContent="flex-end"
              padding="10px"
              marginTop="auto"
              gap="8px"
            >
              <Button
                key="cancel"
                variant="outlined"
                color="gray2"
                onClick={() => handleMoreModal(false)}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        }
      />
    </>
  );
};
